import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { AnnexService } from 'src/app/data/annex.service';
import { Filter } from 'src/app/models/common/filter';
import { AnnexRegistryGridDto } from 'src/app/models/dtos/annexes/annex-registry-grid-dto';
import { AnnexStatusEnum } from 'src/app/models/enums/annex-status-enum';
import { AnnexSyncStatusEnum } from 'src/app/models/enums/annex-sync-status-enum';
import { ScreenService } from 'src/app/core/screen.service';
import { FilterService } from 'src/app/list-filter/services/filter.service';
import { AnnexListFilterService } from '../services/annex-list-filter.service';
import { AnnexRegistryDataSource } from './annexes-registry.datasource';
import { ResizableColumnService } from 'src/app/shared/services/resizable-column.service';
import { SplitComponentService } from 'src/app/split/services/split-component.service';
import { SplitComponentEnum } from 'src/app/common/enums/split-components';
import { AnnexDetailsPanelComponent } from './annex-details-panel/annex-details-panel.component';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { Permission } from 'src/app/common/enums';

@Component({
  selector: 'app-annexes-registry',
  templateUrl: './annexes-registry.component.html',
  styleUrls: ['./annexes-registry.component.scss'],
  providers: [
    ResizableColumnService,
    { provide: FilterService, useClass: AnnexListFilterService },
  ],
  standalone: false,
})
export class AnnexesRegistryComponent implements OnInit, AfterViewInit, OnDestroy {
  screenService = inject(ScreenService);
  private annexService = inject(AnnexService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private filterService = inject(FilterService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private resizableColumnService = inject(ResizableColumnService);
  private splitComponentService = inject(SplitComponentService);
  private dialog = inject(MatDialog);
  private snackBarService = inject(SnackBarService);

  splitComponentId = 'annexes-registry-split';

  sticky = false;

  @ViewChild(CdkVirtualScrollViewport, { static: true }) viewport: CdkVirtualScrollViewport;
  @ViewChild('tableContainer', { static: true }) tableContainerRef: ElementRef;
  @ViewChild(MatSort) sort: MatSort;

  resizableColumnDefinitions = [
    { column: 'Company', width: 200, minWidth: 200 },
    { column: 'EmployerName', width: 200, minWidth: 200 },
    { column: 'EmployerObject', width: 200, minWidth: 200 },
    { column: 'AgreementType', width: 180, minWidth: 180 },
  ];

  tableMinWidth = 2120;

  displayedColumns: string[] = [
    'LastName',
    'Company',
    'EmployerName',
    'EmployerObject',
    'AgreementType',
    'AgreementNumber',
    'AnnexNumber',
    'StartDate',
    'EndDate',
    'AnnexStatus',
    'Actions',
  ];

  dataSource: AnnexRegistryDataSource;
  filters: Filter[] = [];
  isAdmin = false;
  areFiltersExpanded = false;
  activeAnnexId: number | null = null;
  errorImportTaskAnnexIds = new Set<number>();

  page = 1;
  pageSize = 30;

  offset$: Observable<number>;

  AnnexStatusEnum = AnnexStatusEnum;
  AnnexSyncStatusEnum = AnnexSyncStatusEnum;

  getExternalSyncStatusClass(statusId: number | null): string {
    switch (statusId) {
      case AnnexSyncStatusEnum.Pending:   return 'label--orange';
      case AnnexSyncStatusEnum.InProcess: return 'label--blue';
      case AnnexSyncStatusEnum.Success:   return 'label--green';
      case AnnexSyncStatusEnum.Error:     return 'label--red';
      default: return '';
    }
  }

  private readonly defaultSortColumn = 'StartDate';
  private readonly defaultSortDirection = 'desc';
  private readonly unsubscribe$ = new Subject<void>();

  @HostListener('window:resize', ['$event.target'])
  onResize(): void {
    this.resizableColumnService.onResize();
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    if (this.isAdmin) {
      this.displayedColumns.splice(this.displayedColumns.indexOf('Actions'), 0, 'ExternalSystemSyncStatus');
    }
    this.dataSource = new AnnexRegistryDataSource(this.annexService, { viewport: this.viewport });

    this.offset$ = this.viewport.renderedRangeStream.pipe(
      map(() => -this.viewport.getOffsetToRenderedContentStart())
    );

    this.filterService.filters$.pipe(takeUntil(this.unsubscribe$)).subscribe(filters => {
      this.filters = filters;
      this.resetData();
      this.fetchData();
    });

    this.resizableColumnService.setConfig(this.resizableColumnDefinitions, this.tableMinWidth, this.tableContainerRef, this.changeDetectorRef);
    this.resizeOnDataLoaded();
  }

  ngAfterViewInit(): void {
    this.route.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      if (params['annexId']) {
        const parsed = +params['annexId'];
        this.activeAnnexId = isNaN(parsed) ? null : parsed;
        if (this.activeAnnexId != null) {
          this.openAnnexDetailsPanelFromParams(params);
        }
      } else {
        this.activeAnnexId = null;
        this.splitComponentService.removeComponent(this.splitComponentId, SplitComponentEnum.AnnexDetailsPanel);
      }
    });

    this.dataSource.dataLoaded$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this.activeAnnexId != null) {
        this.openAnnexDetailsPanelFromParams({ annexId: this.activeAnnexId });
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.dataSource.disconnect();
  }

  onReEnqueueImportTask(annex: AnnexRegistryGridDto): void {
    this.dialog.open(ConfirmDialogComponent, { data: { message: 'Annex.ReEnqueueConfirm' } })
      .afterClosed().pipe(first(), takeUntil(this.unsubscribe$))
      .subscribe(confirmed => {
        if (!confirmed) { return; }
        this.annexService.reEnqueueImportTask(annex.AnnexId).pipe(first()).subscribe({
          next: () => {
            this.snackBarService.openSuccessSnackBar(this.translateService.instant('Annex.ImportTaskInProgress'));
            this.resetData();
            this.fetchData();
          },
          error: () => this.snackBarService.openErrorSnackBar('SomethingWentWrong'),
        });
      });
  }

  onSkipImportTask(annex: AnnexRegistryGridDto): void {
    this.dialog.open(ConfirmDialogComponent, { data: { message: 'Annex.SkipConfirm' } })
      .afterClosed().pipe(first(), takeUntil(this.unsubscribe$))
      .subscribe(confirmed => {
        if (!confirmed) { return; }
        this.annexService.skipImportTask(annex.AnnexId).pipe(first()).subscribe({
          next: () => {
            this.snackBarService.openSuccessSnackBar(this.translateService.instant('Annex.ImportTaskInProgress'));
            this.resetData();
            this.fetchData();
          },
          error: () => this.snackBarService.openErrorSnackBar('SomethingWentWrong'),
        });
      });
  }

  onMarkAllImportTasksAsManuallyCorrected(annex: AnnexRegistryGridDto): void {
    this.dialog.open(ConfirmDialogComponent, { data: { message: 'Annex.MarkAllConfirm' } })
      .afterClosed().pipe(first(), takeUntil(this.unsubscribe$))
      .subscribe(confirmed => {
        if (!confirmed) { return; }
        this.annexService.markAllImportTasksAsManuallyCorrected(annex.AnnexId).pipe(first()).subscribe({
          next: () => {
            this.snackBarService.openSuccessSnackBar(this.translateService.instant('Annex.ImportTaskInProgress'));
            this.resetData();
            this.fetchData();
          },
          error: () => this.snackBarService.openErrorSnackBar('SomethingWentWrong'),
        });
      });
  }

  fetchData(page?: number): void {
    this.dataSource.fetchAnnexRegistry(
      page ?? this.page,
      this.pageSize,
      this.sort?.active ?? this.defaultSortColumn,
      this.sort?.direction ?? this.defaultSortDirection,
      this.filters,
    );
  }

  nextBatch(event: number): void {
    if (!this.sticky) {
      this.sticky = true;
    }
    const buffer = 20;
    const end = this.viewport.getRenderedRange().end;
    if (!this.dataSource.isLoading && end + buffer > this.page * this.pageSize) {
      this.fetchData(++this.page);
    }
  }

  onSortChange(): void {
    this.resetData();
    this.fetchData();
  }

  resetFilters(): void {
    this.filterService.resetFilters().pipe(first()).subscribe();
  }

  toggleFiltersPanel = (): void => { this.areFiltersExpanded = !this.areFiltersExpanded; };

  getColumnWidth(column: string): number {
    return this.resizableColumnService.getColumnWidth(column);
  }

  get scrollItemPosition(): number {
    return this.viewport?.getRenderedRange()?.end ?? 0;
  }

  trackBy = (_: number, item: AnnexRegistryGridDto) => item.AnnexId;

  navigateToAgreementDetails(item: AnnexRegistryGridDto): void {
    this.router.navigate(['/workers', item.WorkerId, 'employmentType', item.EmploymentTypeId, 'agreements', item.WorkerAgreementId, 'details']);
  }

  navigateToAnnexSignature(item: AnnexRegistryGridDto): void {
    this.router.navigate(['/workers', item.WorkerId, 'employmentType', item.EmploymentTypeId, 'agreements', item.WorkerAgreementId, 'annexes', item.AnnexId, 'signature']);
  }

  openAnnexDetailsPanel(item: AnnexRegistryGridDto): void {
    if (!this.hasDetailsAccess()) { return; }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { annexId: item.AnnexId, annexTab: null },
      queryParamsHandling: 'merge',
    });
  }

  openAnnexDetailsPanelOnChangedTab(item: AnnexRegistryGridDto): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { annexId: item.AnnexId, annexTab: 1 },
      queryParamsHandling: 'merge',
    });
  }

  onAnnexPanelClose(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { annexId: null },
      queryParamsHandling: 'merge',
    });
  }

  isPendingApproval(item: AnnexRegistryGridDto): boolean {
    return item.AnnexStatusId === AnnexStatusEnum.PendingApproval;
  }

  private openAnnexDetailsPanelFromParams(params: Params): void {
    if (!this.hasDetailsAccess()) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          annexId: null,
          annexTab: null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });

      return;
    }

    const id = +params['annexId'];
    if (isNaN(id)) return;
    const selectedTabIndex = +(params['annexTab'] ?? 0);
    const { prevId, nextId } = this.getAdjacentAnnexIds(id);
    this.splitComponentService.addComponent(this.splitComponentId, {
      id: SplitComponentEnum.AnnexDetailsPanel,
      componentType: AnnexDetailsPanelComponent,
      direction: 'horizontal',
      data: { annexId: id, selectedTabIndex },
      size: 33,
      header: {
        closeButton: true,
        leftButtons: [
          {
            icon: 'chevron_left',
            disabled: prevId == null,
            title: this.translateService.instant('Annex.PreviousAnnex'),
            onClick: () =>
              prevId != null &&
              this.router.navigate([], { relativeTo: this.route, queryParams: { annexId: prevId }, queryParamsHandling: 'merge' }),
          },
          {
            icon: 'chevron_right',
            disabled: nextId == null,
            title: this.translateService.instant('Annex.NextAnnex'),
            onClick: () =>
              nextId != null &&
              this.router.navigate([], { relativeTo: this.route, queryParams: { annexId: nextId }, queryParamsHandling: 'merge' }),
          },
        ],
      },
    });
  }

  private getAdjacentAnnexIds(currentId: number): { prevId: number | null; nextId: number | null } {
    const all = this.dataSource?.allData ?? [];
    const idx = all.findIndex(a => a.AnnexId === currentId);
    if (idx === -1) return { prevId: null, nextId: null };
    return {
      prevId: idx > 0 ? all[idx - 1].AnnexId : null,
      nextId: idx < all.length - 1 ? all[idx + 1].AnnexId : null,
    };
  }

  private resetData(): void {
    this.page = 1;
    this.dataSource.reset();
  }

  private resizeOnDataLoaded(): void {
    this.dataSource.dataLoaded$.pipe(first()).subscribe(() => {
      this.resizableColumnService.onResize();
    });
  }

  private readonly detailsPermissions = [Permission.ManageMyExternalWorkersList, Permission.ViewAllExternalWorkers];

  hasDetailsAccess = (): boolean =>
    this.authService.hasAnyPermission(this.detailsPermissions);

  hasAnyActions = (element: AnnexRegistryGridDto): boolean =>
    this.hasDetailsAccess() || (this.isAdmin && element.AnnexSyncStatusId === AnnexSyncStatusEnum.Error) || this.isPendingApproval(element);
}
